import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { AccountSummary } from './auth.models';
import { SettingsService, SETTINGS_KEYS } from '../services/settings.service';

@Injectable({ providedIn: 'root' })
export class SessionState {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly settings = inject(SettingsService);

  readonly account = signal<AccountSummary | null>(
    this.settings.get<AccountSummary>(SETTINGS_KEYS.AUTH_USER)
  );
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  readonly isAuthenticated = computed(() => !!this.account());

  readonly userEmail = () => this.account()?.email ?? null;
  readonly userFullName = () => {
    const acc = this.account();
    if (!acc) return null;
    return `${acc.firstName} ${acc.lastName}`.trim();
  };

  constructor() {
    if (this.isAuthenticated() && this.router.url.startsWith('/login')) {
      this.router.navigate(['/dashboard']);
    }
  }

  async login(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const firebaseToken = await this.authService.signInWithMicrosoft();
      const response = await this.authService.login(firebaseToken);
      this.settings.set(SETTINGS_KEYS.AUTH_USER, response.account);
      this.account.set(response.account);
      await this.router.navigate(['/dashboard']);
    } catch (err: unknown) {
      const error = err as { code?: string; error?: { message?: string }; message?: string };
      if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
        return;
      }
      const msg = error?.error?.message ?? error?.message ?? 'Error al iniciar sesión';
      this.error.set(msg);
    } finally {
      this.loading.set(false);
    }
  }

  async logout(): Promise<void> {
    try {
      await this.authService.logout();
    } finally {
      this.settings.remove(SETTINGS_KEYS.AUTH_USER);
      this.account.set(null);
      this.error.set(null);
      await this.router.navigate(['/login']);
    }
  }
}
